import type {
  BlockRule,
  LogicBlockConfig,
  LogicBlockIndex,
  LogicBlockRecord,
  OptionsAsArgument,
} from '@morph-mapper/node-logic';
import { parseNodeOptions } from '@/utils';
import { OptionType } from '@morph-mapper/types';
import type { EdgeInstance, Graph, GraphId, NodeId } from '../types/graph';

const findParentNodeId = (graph: Graph, nodeId: NodeId): NodeId | undefined => {
  const parentEdge = Object.values(graph.edges).find(
    (edge) => edge.from.nodeId === nodeId,
  );
  return parentEdge?.to.nodeId;
};

const findChildEdges = (graph: Graph, nodeId: NodeId): EdgeInstance[] => {
  return Object.values(graph.edges)
    .filter((edge) => edge.to.nodeId === nodeId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const resolveRootNodeId = (graph: Graph, rootNodeId?: NodeId): NodeId => {
  if (rootNodeId) {
    return rootNodeId;
  }

  const nodeIds = Object.keys(graph.nodes);
  if (nodeIds.length === 0) {
    throw new Error('Cannot evaluate graph without nodes.');
  }

  const nodeHasParent = new Set<NodeId>();
  Object.values(graph.edges).forEach((edge) => {
    nodeHasParent.add(edge.from.nodeId);
  });

  const rootId = nodeIds.find((nodeId) => !nodeHasParent.has(nodeId));
  if (rootId) {
    return rootId;
  }

  return nodeIds[0];
};

const isSemanticRootNode = (
  graph: Graph,
  nodeId: NodeId,
  resolvedRootId: NodeId,
  parentId?: NodeId,
) => {
  if (parentId === undefined || nodeId === resolvedRootId) {
    return true;
  }

  const resolvedRoot = graph.nodes[resolvedRootId];
  if (!resolvedRoot) {
    return false;
  }

  // The terminal node is a transport wrapper for field graphs.
  // The node feeding it should be treated as the logical root.
  return resolvedRoot.kind === 'terminal' && parentId === resolvedRootId;
};

const normalizeOptions = <T extends LogicBlockIndex = LogicBlockIndex>(
  options: OptionsAsArgument<
    LogicBlockConfig<T, Set<BlockRule>, any>['options']
  >,
  resolveGraph: (graphId: GraphId) => unknown,
): {
  options: OptionsAsArgument<
    LogicBlockConfig<T, Set<BlockRule>, any>['options']
  >;
  optionTypes: Record<string, OptionType>;
} => {
  if (!options) {
    return { options: {} as any, optionTypes: {} };
  }

  const entries = Object.entries(options) as Array<
    [string, { type?: unknown } | { value?: unknown } | unknown]
  >;
  const hasWrappedValues = entries.some(
    ([, entry]) =>
      entry !== null && typeof entry === 'object' && 'type' in entry,
  );

  if (!hasWrappedValues) {
    return { options, optionTypes: {} };
  }

  const optionTypes = entries.reduce<Record<string, OptionType>>(
    (acc, [key, entry]) => {
      if (entry !== null && typeof entry === 'object' && 'type' in entry) {
        acc[key] = (entry as { type: OptionType }).type;
      }
      return acc;
    },
    {},
  );

  return {
    options: parseNodeOptions(options, resolveGraph),
    optionTypes,
  };
};

export const compileLogicTree = <
  K extends Record<LogicBlockIndex, any>,
  T extends LogicBlockRecord<K, Set<BlockRule>>,
>(
  graph: Graph,
  logicBlocks: T,
  resolveGraph: (graphId: GraphId) => unknown,
  rootNodeId?: NodeId,
) => {
  const resolvedRootId = resolveRootNodeId(graph, rootNodeId);

  const renderNode = (nodeId: NodeId): any => {
    const node = graph.nodes[nodeId];
    if (!node) {
      throw new Error(`Node ${nodeId} not found in graph.`);
    }

    const childEdges = findChildEdges(graph, nodeId);
    const childTemplates = childEdges.map((edge) => ({
      portId: edge.to.portId,
      template: renderNode(edge.from.nodeId),
    }));
    const parentId = findParentNodeId(graph, nodeId);
    const { options, optionTypes } = normalizeOptions(
      node.options,
      resolveGraph,
    );
    const { mutation, ports } = logicBlocks[node.kind];
    const inputPorts = ports?.input ?? [];
    const inputs = inputPorts.reduce<Record<string, unknown>>((acc, port) => {
      const values = childTemplates
        .filter((child) => child.portId === port.id)
        .map((child) => child.template);
      acc[port.id] = port.multi ? values : values[0];
      return acc;
    }, {});

    const template = mutation({
      inputs,
      options,
      ctx: {
        isRoot: isSemanticRootNode(graph, nodeId, resolvedRootId, parentId),
      },
      optionTypes,
    });

    return template;
  };

  return renderNode(resolvedRootId);
};
